<template>
  <div v-if="pending" class="space-y-4">
    <FeedCommentsSkeletonLoader />
    <FeedCommentsSkeletonLoader />
    <FeedCommentsSkeletonLoader />
  </div>
  <div v-else-if="comments">
    <div>
      <p
        v-if="comments.length === 0"
        class="text-center text-neutral-700 dark:text-neutral-300 my-6"
      >
        {{ $t("components.feedCommentPreview.emptyState") }}
      </p>
      <div v-else class="py-2 space-y-4 sm:mx-auto" ref="feedListContainer">
        <FeedCommentContent
          v-for="comment in comments"
          :comment="comment"
          :feed-type="feedType"
          :feed-id="feedId"
        >
          <template #options>
            <!-- Comment actions -->
          </template>
        </FeedCommentContent>
      </div>
    </div>
  </div>
  <NamiModalPresets
    v-model="showNotLoggedInModal"
    :options="{
      title: 'You need to log in to do this.',
      icon: 'wink',
      buttons: [
        { buttonType: 'primary', buttonText: 'Login', onButtonClick: doLogin },
        {
          buttonType: 'secondary',
          buttonText: 'Cancel',
          onButtonClick: () => (showNotLoggedInModal = false),
        },
      ],
    }"
  />
  <ReportModal
    v-model="showReportModal"
    entityType="comment"
    :entityId="selectedCommentId"
  />
</template>

<script lang="ts" setup>
import {
  Comment,
  Reaction,
  getRelationship,
  type CommentableResource,
  type ReactionType,
} from "~/src/api";
import { bulkAddCommentMeta } from "~/composables/comment/useCommentMeta";

interface Props {
  feedType: CommentableResource;
  feedId: string;
}

const props = defineProps<Props>();

const route = useRoute();
const nuxtApp = useNuxtApp();
const authStore = nuxtApp.$auth();
const showNotLoggedInModal = ref(false);
const showReportModal = ref(false);
const selectedCommentId = ref("");

const {
  pending,
  error,
  refresh,
  data: comments,
} = useAsyncData(`feed-preview-${props.feedId}`, async () => {
  const comments = await Comment.search(props.feedType, props.feedId, {
    parentId: "NULL",
    order: { order: "desc" },
    includes: ["user", "organization", "profile_frame", "badge"],
    limit: 3,
  });

  const commentIds = comments.data.map((x) => x.id);

  const [reactions, userReactions] = await Promise.all([
    Reaction.getReactionSummaryBatch("comment", {
      entityIds: commentIds,
    }),
    authStore?.userEntity
      ? Reaction.findReactions("comment", {
          userIds: [authStore.userEntity.id],
          entityIds: commentIds,
          limit: commentIds.length,
        })
      : null,
  ]);

  // create an array of reaction data
  const mappedMeta = commentIds.map((id) => {
    // get the reaction for the specified comment id
    const associatedReactions = reactions.data.find(
      ({ id }) => id.replace("comment-", "") === id,
    );

    // format the response
    return {
      commentId: id,
      userReaction: userReactions
        ? (userReactions.data.find(
            (r) => getRelationship(r, "comment").id === id,
          )?.attributes.reactionType ?? null)
        : null,
      allReactions: associatedReactions?.attributes.reactions ?? {},
    };
  });

  // update meta db
  await bulkAddCommentMeta(mappedMeta);

  return comments.data;
});
if (error.value) {
  console.error(error.value);
}

function doLogin() {
  authStore?.login({
    redirectBackTo: route.fullPath,
    locale: reverseLocaleMap(useI18n().locale.value),
  });
}

watch(
  () => props.feedId,
  () => refresh(),
);
</script>
